import {
  getPageHeader,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  useThreads,
  VisibleFilterFields,
  SegmentedFilterChip,
} from '@yeshuman/sdk'
import { useState, useEffect, useRef } from 'react'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { Lightbulb, X, MessageSquare } from 'lucide-react'
import { Insight, useInsights, useDeleteInsight, InsightFilters } from '../hooks/use-insights'
import { InsightCard } from './insight-card'
import { toast } from 'sonner'
import { useSearchParams } from 'react-router-dom'
import { getHealthInsightFilterDefinitions } from '../list-filters/insights'

const insightFilterDefinitions = getHealthInsightFilterDefinitions()

export function InsightsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Read thread parameter from URL
  const threadIdFromUrl = searchParams.get('thread')
  
  const [filters, setFilters] = useState<InsightFilters>({})
  const [deletingInsightId, setDeletingInsightId] = useState<string | null>(null)
  
  // Track previous insights for agent update animation
  const [highlightedInsights, setHighlightedInsights] = useState<Set<string>>(new Set())
  const previousInsightsRef = useRef<Insight[]>([])

  // Sync thread_id from URL to filters
  useEffect(() => {
    if (threadIdFromUrl) {
      setFilters(prev => ({ ...prev, thread_id: threadIdFromUrl }))
    } else {
      // Remove thread_id from filters if not in URL
      setFilters(prev => {
        const { thread_id, ...rest } = prev
        return rest
      })
    }
  }, [threadIdFromUrl])

  // Fetch threads to get thread name for display
  const { threads } = useThreads()
  const currentThread = threads?.find(t => t.id === threadIdFromUrl)

  const { insights, isLoading, error, refetch } = useInsights(filters)
  const deleteInsight = useDeleteInsight()

  // Detect agent updates and highlight changed insights
  useEffect(() => {
    if (insights && previousInsightsRef.current.length > 0) {
      const changedIds = new Set<string>()
      
      // Find new insights
      insights.forEach((insight) => {
        const prev = previousInsightsRef.current.find((i) => i.id === insight.id)
        if (!prev) {
          changedIds.add(insight.id)
        } else {
          // Check if insight was updated (compare JSON strings)
          if (JSON.stringify(prev) !== JSON.stringify(insight)) {
            changedIds.add(insight.id)
          }
        }
      })
      
      if (changedIds.size > 0) {
        setHighlightedInsights(changedIds)
        // Fade out highlights after 3 seconds
        setTimeout(() => {
          setHighlightedInsights(new Set())
        }, 3000)
      }
    }
    
    // Update ref (doesn't trigger re-render)
    if (insights) {
      previousInsightsRef.current = insights
    }
  }, [insights])

  const handleDelete = async (insightId: string) => {
    try {
      await deleteInsight.mutateAsync(insightId)
      toast.success('Insight deleted successfully')
      setDeletingInsightId(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete insight')
    }
  }

  const handleClearThreadFilter = () => {
    const params = new URLSearchParams(searchParams)
    params.delete('thread')
    setSearchParams(params)
  }

  // Group insights by status
  const groupInsightsByStatus = () => {
    const groups = {
      active: [] as Insight[],
      emerging: [] as Insight[],
      improving: [] as Insight[],
      resolved: [] as Insight[],
      monitoring: [] as Insight[],
    }

    insights.forEach((insight) => {
      if (groups[insight.status as keyof typeof groups]) {
        groups[insight.status as keyof typeof groups].push(insight)
      }
    })

    return groups
  }

  const groupedInsights = groupInsightsByStatus()
  const { title: baseTitle, description: baseDesc } = getPageHeader('/insights', 'Insights', 'Higher-level health patterns identified by analyzing multiple data points.')

  return (
    <PageContainer>
      <PageHeader
          leading={<PageHeaderLeading />}
          title={
            <>
              {baseTitle}
              {threadIdFromUrl && (
                <Badge variant="outline" className="ml-2">
                  <MessageSquare className="h-3.5 w-3.5 mr-1" />
                  Thread Filter
                </Badge>
              )}
            </>
          }
          description={
            threadIdFromUrl
              ? currentThread?.subject
                ? `Insights from "${currentThread.subject}"`
                : 'Insights from thread'
              : baseDesc
          }
          icon={<Lightbulb className="h-5 w-5" />}
        />

        {/* Filters */}
        <VisibleFilterFields
          listId="healthInsights"
          filters={filters}
          onFiltersChange={setFilters}
          definitions={insightFilterDefinitions}
          ctx={undefined}
          betweenSlot={
            threadIdFromUrl ? (
              <div className="flex flex-wrap items-center gap-2">
                <SegmentedFilterChip
                  tint="muted"
                  label="Thread"
                  value={currentThread?.subject || `${threadIdFromUrl.slice(0, 8)}…`}
                  onRemove={handleClearThreadFilter}
                  removeAriaLabel="Clear thread filter"
                />
              </div>
            ) : undefined
          }
        />

        {/* Error State */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">Failed to load insights: {error.message}</p>
              <div className="mt-4">
                <Button variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {isLoading ? <HealthListPageSkeleton showFilters={false} ariaLabel="Loading insights" /> : null}

        {/* Insights List */}
        {!isLoading && !error && (
          <div className="space-y-6">
            {insights.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Lightbulb className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    {threadIdFromUrl ? (
                      <>
                        <h3 className="text-lg font-semibold mb-2">No insights found for this thread</h3>
                        <p className="text-muted-foreground mb-4">
                          {currentThread?.subject 
                            ? `No insights have been identified in "${currentThread.subject}" yet.`
                            : 'No insights have been identified in this thread yet.'}
                        </p>
                        <Button variant="outline" onClick={handleClearThreadFilter}>
                          <X className="h-4 w-4 mr-2" />
                          Clear Thread Filter
                        </Button>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold mb-2">No insights identified yet</h3>
                        <p className="text-muted-foreground mb-4">
                          Insights are automatically generated by the agent when patterns are detected in your health data.
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {groupedInsights.active.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Active</h2>
                    <div className="grid gap-4">
                      {groupedInsights.active.map((insight) => (
                        <InsightCard
                          key={insight.id}
                          insight={insight}
                          onDelete={(id) => setDeletingInsightId(id)}
                          isHighlighted={highlightedInsights.has(insight.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedInsights.emerging.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Emerging</h2>
                    <div className="grid gap-4">
                      {groupedInsights.emerging.map((insight) => (
                        <InsightCard
                          key={insight.id}
                          insight={insight}
                          onDelete={(id) => setDeletingInsightId(id)}
                          isHighlighted={highlightedInsights.has(insight.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedInsights.improving.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Improving</h2>
                    <div className="grid gap-4">
                      {groupedInsights.improving.map((insight) => (
                        <InsightCard
                          key={insight.id}
                          insight={insight}
                          onDelete={(id) => setDeletingInsightId(id)}
                          isHighlighted={highlightedInsights.has(insight.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedInsights.monitoring.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Monitoring</h2>
                    <div className="grid gap-4">
                      {groupedInsights.monitoring.map((insight) => (
                        <InsightCard
                          key={insight.id}
                          insight={insight}
                          onDelete={(id) => setDeletingInsightId(id)}
                          isHighlighted={highlightedInsights.has(insight.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedInsights.resolved.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Resolved</h2>
                    <div className="grid gap-4">
                      {groupedInsights.resolved.map((insight) => (
                        <InsightCard
                          key={insight.id}
                          insight={insight}
                          onDelete={(id) => setDeletingInsightId(id)}
                          isHighlighted={highlightedInsights.has(insight.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deletingInsightId !== null} onOpenChange={(open) => !open && setDeletingInsightId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete insight</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this insight? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deletingInsightId && handleDelete(deletingInsightId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
  )
}
